



































































import Vue from 'vue'
import Analytics from '@/services/Analytics'
import { mapGetters } from 'vuex'
import InvoiceCampaign, { InvoiceCampaignEvent } from '@/types/InvoiceCampaign.interface'
import InvoicesPlaceholder from '@/components/InvoicesPlaceholder.vue'
import IListAction, { IListActionType } from '@/types/components/IListAction.interface'
import Button from '@/types/components/Button.interface'
import IListTile from '@/types/components/IListTile.interface'
import IListIcon, { IListIconType } from '@/types/components/IListIcon.interface'
import moment from 'moment-timezone'
import { randomKey, validateTimezone } from '@/utility/helpers'
import SmsInboundBubble from '@/components/SmsInboundBubble.vue'
import SmsOutboundBubble from '@/components/SmsOutboundBubble.vue'
export default Vue.extend({
    components: { InvoicesPlaceholder, SmsInboundBubble, SmsOutboundBubble },
    data() {
        return {
            campaignSchedule: {},
            loading: false,
            toolbar: {
                //  Title toolbar
                title: 'Payments',
                help: {
                    title: 'Automated Payments',
                    body: 'Automate your QuickBooks invoice collection and reminders by turning on SMS automation',
                    button: {
                        text: 'Learn More',
                        link: 'https://learn.messagedesk.com/en/articles/5473471-payments-automation-overview'
                    }
                }
            }
        }
    },
    beforeDestroy() {
        //  Set our selected event to null
        this.selectedEventMessage = null
    },
    mounted() {
        //  If invoice campaigns are enabled, we want to validate the timezone
        if (this.invoiceCampaignsEnabled) {
            //  Force tenant to choose timezone before enabling InvoiceCampaigns
            validateTimezone()
        }
    },
    computed: {
        // ...mapGetters(['hasInvoices', 'invoiceCampaign', 'isSphere']),
        ...mapGetters({
            hasInvoices: 'hasInvoices',
            invoiceCampaign: 'invoiceCampaign',
            isSphere: 'isSphere',
            tenant: 'tenant'
        }),
        filteredEvents(): InvoiceCampaignEvent[] {
            //  If invoice campaign is not loaded, do nothing
            if (!this.invoiceCampaign) return []

            //  Get the events from the invoice campaign
            const events = this.invoiceCampaign.events

            //  Attach a random id to each event so that the IList doesn't think they are all the same items when selected
            events.forEach((event: InvoiceCampaignEvent) => {
                if (!event.id) event.id = randomKey()
            })

            //  Return the events with attached ids
            return events
        },
        eventFlows() {
            //  Define the tenant's data provider
            const provider = this.isSphere ? 'MESSAGEDESK' : this.tenant.data_provider

            //  Select the readable data provider name
            const providerName = {
                MESSAGEDESK: 'MessageDesk',
                QBO: 'QuickBooks Online',
                XERO: 'Xero'
            }[provider]

            return {
                invoiceCreated: [
                    {
                        class: 'inbound',
                        type: provider,
                        text: `<span style="font-weight:bold">Event:</span><br>Invoice created in ${providerName}`
                    },
                    {
                        class: 'inbound',
                        type: 'MESSAGEDESK',
                        text: `<span style="font-weight:bold">Trigger:</span><br>MessageDesk receives the event from ${providerName}`,
                        hidden: provider == 'MESSAGEDESK'
                    },
                    {
                        class: 'outbound',
                        type: 'autoresponder',
                        text: `<span style="font-weight:bold">Text Message Sent:</span><br>${this.selectedEventMessage.message}`
                    }
                ],
                invoicePaid: [
                    {
                        class: 'inbound',
                        type: provider,
                        text: `<span style="font-weight:bold">Event:</span><br>Invoice paid in ${providerName}`
                    },
                    {
                        class: 'inbound',
                        type: 'MESSAGEDESK',
                        text: `<span style="font-weight:bold">Trigger:</span><br>MessageDesk receives the event from ${providerName}`,
                        hidden: provider == 'MESSAGEDESK'
                    },
                    {
                        class: 'outbound',
                        type: 'autoresponder',
                        text: `<span style="font-weight:bold">Text Message Sent:</span><br>${this.selectedEventMessage.message}`
                    }
                ],
                reminder: [
                    {
                        class: 'inbound',
                        type: provider,
                        text: `<span style="font-weight:bold">Event:</span><br>Invoice ${
                            this.selectedEventMessage.field == 'due_date'
                                ? 'created'
                                : 'paid'
                        } in ${providerName}`
                    },
                    {
                        class: 'inbound',
                        type: 'MESSAGEDESK',
                        text: `<span style="font-weight:bold">Trigger:</span><br>${this.reminderTrigger()}`
                    },
                    {
                        class: 'outbound',
                        type: 'autoresponder',
                        text: `<span style="font-weight:bold">Text Message Sent:</span><br>${this.selectedEventMessage.message}`
                    }
                ]
            }
        },
        invoiceCampaignsEnabled(): boolean {
            return this.$store.getters.getEnabled('payment_automation')
        },
        selectedEventMessage: {
            get(): InvoiceCampaignEvent {
                return this.$store.getters.invoiceCampaignSelectedEvent
            },
            set(val: InvoiceCampaignEvent): void {
                this.$store.commit('invoiceCampaignSelectedEventSet', val)
            }
        }
    },
    methods: {
        editScheduleTime(): void {
            //  Create a sample model
            const model = {
                time: new Date().setHours(
                    parseInt(this.invoiceCampaign.time.substring(0, 2)),
                    parseInt(this.invoiceCampaign.time.substring(3, 5))
                )
            }

            //  Pop our edit form
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                component: 'app-form',
                title: 'Change send time',
                icon: 'bi-gear',
                color: 'primary',
                description:
                    'This is the time of day your invoice reminders will be sent at. Invoice reminders that are sent immediately will not be affected by this time.',
                props: {
                    model: model,
                    inputs: [
                        {
                            type: 'DATETIME',
                            name: 'Time',
                            title: 'Time',
                            label: 'Time',
                            path: 'time',
                            timeOnly: true,
                            validate: 'required',
                            class: 'mb-4'
                        }
                    ]
                },
                callback: (res: InvoiceCampaign) => this.onScheduleEdited(res)
            })
        },
        eventAction(eventMessage: InvoiceCampaignEvent): IListAction {
            //  Our container for our menu of buttons
            const menu: Button[] = [
                {
                    icon: 'bi-play-circle-fill',
                    color: 'primary',
                    text: 'Activate',
                    hidden: eventMessage.paused === null,
                    callback: () => this.eventMessageResume(eventMessage)
                },
                {
                    icon: 'bi-pause-circle-fill',
                    color: 'primary',
                    text: 'Pause',
                    hidden: eventMessage.paused !== null,
                    callback: () => this.eventMessagePause(eventMessage)
                },
                {
                    icon: 'bi-pencil-fill',
                    color: 'primary',
                    text: 'Edit',
                    callback: () => this.editEventMessage(eventMessage)
                },
                {
                    icon: 'bi-trash-fill',
                    color: 'primary',
                    text: 'Delete',
                    hidden: eventMessage.event !== 'reminder',
                    callback: () => this.removeEventMessage(eventMessage)
                }
            ]

            //  Create our IListAction
            const action: IListAction = {
                type: IListActionType.menu,
                menu: menu
            }

            //  Return action
            return action
        },
        eventDescription(eventMessage: InvoiceCampaignEvent): string {
            //  GRab the event from our invoice campaign event
            const event = eventMessage.event

            //  Create our reminder trigger string
            const reminder = this.reminderTrigger(eventMessage).toLowerCase()

            //  Our description depends on the event type
            const description = {
                invoiceCreated: `Automatically sends a text message when an unpaid invoice is created`,
                invoicePaid: `Automatically sends a "Thank You" message when an invoice is paid`,
                reminder: `Automatically sends a text message ${reminder}`
            }[event]

            //  Return our parsed description
            return description
        },
        eventHeader(eventMessage: InvoiceCampaignEvent): string {
            //  Our status depends on if our event is paused or not
            const status = eventMessage.paused ? 'Paused' : 'Active'

            //  Our color depends on if we are paused or not
            const color = eventMessage.paused
                ? this.$colors.mdYellow
                : this.$colors.mdPurple

            //  Create our icon and style
            const icon = 'bi-circle-fill'
            const style = `color:${color}; padding-right:4px;`

            //  Append our vars to the html string
            const html = `<span><span class="mdi ${icon}" style="${style}"></span><span>${status}</span></span>`

            //  Return our HTML version of our header
            return html
        },
        eventIcon(): IListIcon {
            //  Our icon is always the same
            const icon = 'bi-lightning-fill'

            //  Create our list icon
            const listIcon: IListIcon = {
                type: IListIconType.icon,
                content: icon,
                color: 'primary'
            }

            //  Return our created icon
            return listIcon
        },
        eventName(eventMessage: InvoiceCampaignEvent): string {
            return eventMessage.name
        },
        eventSendAt(eventMessage: InvoiceCampaignEvent): string {
            //  If not a 'reminder', disregard
            if (eventMessage.event != 'reminder') {
                return `<span class="bi-alarm pr-2"></span><span>Sends immediately</span>`
            }

            //  Create a new date/time from the invoice campaign's time
            const date = new Date().setHours(
                parseInt(this.invoiceCampaign.time.substring(0, 2)),
                parseInt(this.invoiceCampaign.time.substring(3, 5))
            )

            //  Get a time-only string from the date
            const time = moment.utc(date).local().format('hh:mma')

            //  Return the string
            return `<span class="bi-alarm pr-2"></span><span>Sends at ${time}</span>`
        },
        editEventMessage(eventMessage: InvoiceCampaignEvent): void {
            //  Our container for our edited event message
            let editing = eventMessage || this.newEventMessage()

            //  If our container is still emptry, do nothing
            if (!editing) return

            //  Pop our create dialog
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                component: 'invoice-campaign-form',
                width: '540px',
                title: !editing.name
                    ? 'New Invoice Reminder'
                    : `${this.eventName(editing)} - (edit)`,
                icon: 'bi-pencil-fill',
                color: 'primary',
                props: {
                    eventMessage: editing
                },
                callback: (eventMessage) => {
                    this.onEventMessageEdited(eventMessage)
                }
            })
        },
        eventMessagePause(eventMessage: InvoiceCampaignEvent): void {
            //  Pause the event message
            eventMessage.paused = moment().toISOString(true)

            //  Find the index of the event message we're updating
            const i = this.invoiceCampaign.events.findIndex(
                (x: InvoiceCampaignEvent) => x.id == eventMessage.id
            )

            //  Update the eventMessage
            this.invoiceCampaign.events[i] = eventMessage

            //  Send the change to the server
            this.invoiceCampaignUpdate(this.invoiceCampaign)
        },
        eventMessageResume(eventMessage: InvoiceCampaignEvent): void {
            //  Report acttivity to analytics
            Analytics.track('InvoiceReminderEnabled')

            //  Resume the event message
            eventMessage.paused = null

            //  Find the index of the event message we're updating
            const i = this.invoiceCampaign.events.findIndex(
                (x: InvoiceCampaignEvent) => x.id == eventMessage.id
            )

            //  Update the eventMessage
            this.invoiceCampaign.events[i] = eventMessage

            //  Send the change to the server
            this.invoiceCampaignUpdate(this.invoiceCampaign)
        },
        invoiceCampaignEventSelected(event: InvoiceCampaignEvent): void {
            //  Set selected event in left panel
            this.selectedEventMessage = event

            //  Switch view to selected event for mobile views
            this.$refs.invoiceCampaignPanel.step = 2
        },
        invoiceCampaignTile(): IListTile {
            return {
                actionContent: (event: InvoiceCampaignEvent) => this.eventAction(event),
                actionHeader: (event: InvoiceCampaignEvent) => this.eventHeader(event),
                leftIcon: () => this.eventIcon(),
                line1: (event: InvoiceCampaignEvent) => this.eventName(event),
                line2: (event: InvoiceCampaignEvent) => this.eventDescription(event),
                line3: (event: InvoiceCampaignEvent) => this.eventSendAt(event),
                callback: (event: InvoiceCampaignEvent) =>
                    this.invoiceCampaignEventSelected(event)
            }
        },
        async invoiceCampaignUpdate(invoiceCampaign: InvoiceCampaign): Promise<void> {
            //  Start loading
            this.loading = true

            //  Send the request
            const resp = await this.$store.dispatch('invoiceCampaignPut', invoiceCampaign)

            //  Stop loading
            this.loading = false

            //  If the response does NOT exist, do nothing
            if (!resp) return

            //  Else, notify user of success
            this.$store.commit('appNotificationBarSet', {
                message: 'Invoice Reminder Updated'
            })
        },
        newEventMessage(): InvoiceCampaignEvent {
            //  Create defaut event message
            return {
                id: randomKey(),
                event: 'reminder',
                name: null,
                message: null,
                paused: null,
                days: null,
                when: null,
                field: null
            }
        },
        onEventMessageEdited(eventMessage: InvoiceCampaignEvent): void {
            //  Find the index of the eventMessage we're updating
            const i = this.invoiceCampaign.events.findIndex(
                (x: InvoiceCampaignEvent) => x.id == eventMessage.id
            )

            //  If the index is NOT found, we are creating a new eventMessage.
            if (i == -1) {
                //  Add the reminder to the invoice campaign
                this.invoiceCampaign.events.push(eventMessage)

                //  Report activity to analytics
                Analytics.track('InvoiceCampaignReminderCreated', {
                    invoice_campaign_id: this.invoiceCampaign.id
                })
            }
            //  Else, update the invoice campaign data
            else this.invoiceCampaign.events[i] = eventMessage

            //  Update the selected event message
            this.selectedEventMessage = eventMessage

            //  Send the change to the server
            this.invoiceCampaignUpdate(this.invoiceCampaign)

            //  Close the app dialog
            this.$store.dispatch('dialogClose', 'APP')
        },
        async onScheduleEdited(model: InvoiceCampaign): Promise<void> {
            //  Update our invoice campaign
            await this.$store.dispatch('invoiceCampaignPut', {
                ...this.invoiceCampaign,
                time: moment.utc(model.time).local().format('HH:mm')
            })

            //  Notify user of change
            this.$store.commit('appNotificationBarSet', {
                message: 'Invoice reminders updated'
            })

            //  Close our dialog
            this.$store.dispatch('dialogClose', 'APP')
        },
        reminderTrigger(eventMessage: InvoiceCampaignEvent): string {
            //  Grab our event either from the param or our selected event
            const event = eventMessage || this.selectedEventMessage

            //  If the event is due on due date, then keep it simple and return on invoice due date
            if (event.days == 'On due date') return 'On invoice due date'

            //  Else we need to list the days and when it is set to trigger
            return `${event.days} day(s) ${event.when} invoice ${
                event.field == 'due_date' ? 'due date' : 'paid date'
            }`
        },
        removeEventMessage(eventMessage: InvoiceCampaignEvent): void {
            //  Find the index of the invoice campaign events to remove
            const index = this.invoiceCampaign.events.findIndex(
                (event: InvoiceCampaignEvent) => event.id == eventMessage.id
            )

            //  Remove the index
            this.invoiceCampaign.events.splice(index, 1)

            //  If the eventMessage we deleted is selected, unselect it
            if (
                this.selectedEventMessage &&
                eventMessage.id == this.selectedEventMessage.id
            ) {
                this.selectedEventMessage = null
            }

            //  Update the invoice campaign
            this.invoiceCampaignUpdate(this.invoiceCampaign)

            //  Pop confirmation notification
            this.$store.commit('appNotificationBarSet', {
                message: 'Invoice Reminder Deleted'
            })
        }
    },
    watch: {
        invoiceCampaignsEnabled(bool: boolean): void {
            if (bool) {
                //  If the tenant's invoice campaign is NOT loaded, get it
                this.$store.dispatch('invoiceCampaignGet')

                //  Force tenant to choose timezone before enabling InvoiceCampaigns
                validateTimezone()
            }
        }
    }
})
